import { useEffect, useState } from "react";
import { MobileSheet } from "../../components/MobileSheet.tsx";
import { MobileStatusMetric } from "../../components/MobileStatusMetric.tsx";
import { MobileEntityRow } from "../../mobile/mobilePrimitives.tsx";
import { helpers } from "../../util/helpers.ts";
import i18n from "../../util/i18n.ts";
import { translateInjuryName } from "../../util/injuryNames.ts";
import { createMobileReturnUrl } from "../../util/mobilePageContext.ts";
import { formatPlayerName } from "../../util/playerNames.ts";
import { PositionText } from "../../util/positionNames.tsx";
import { toWorker } from "../../util/toWorker.ts";
import {
  getRosterInjuryImpactStatus,
  type RosterInjuryImpactState,
} from "../Player/playerStatusMetrics.ts";

// M3-1: the injuries page became this bottom-sheet, mounted on the roster (team)
// tab. It shows the CURRENT team's CURRENT injuries only; data is fetched ad hoc
// on open via the `getInjuriesSheet` worker RPC (the M3 data pattern), not the
// page-view pipeline. See MOBILE_SHEET_CONTRACT_V1 in MobileSheet.tsx.

type InjuryRow = {
  pid: number;
  firstName: string;
  firstNameShort?: string;
  lastName: string;
  pos: string;
  type: string;
  games: number;
};

const getMobileScrollY = () => (typeof window !== "undefined" ? window.scrollY : undefined);

const getInjuryImpactText = (state: RosterInjuryImpactState) => {
  const key =
    state === "healthy"
      ? "impactHealthy"
      : state === "minor"
        ? "impactMinor"
        : state === "limited"
          ? "impactLimited"
          : "impactMajor";

  return i18n.t(`injuries.${key}`, { ns: "ui" });
};

export const InjuriesSheet = ({
  onClose,
  open,
  teamName,
  tid,
}: {
  onClose: () => void;
  open: boolean;
  teamName: string;
  tid: number;
}) => {
  const [injuries, setInjuries] = useState<InjuryRow[] | undefined>(undefined);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setInjuries(undefined);
    (async () => {
      const data = await toWorker("main", "getInjuriesSheet", tid);
      if (!cancelled) {
        setInjuries(data.injuries);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, tid]);

  const playerHref = (pid: number) =>
    createMobileReturnUrl(helpers.leagueUrl(["player", pid]), {
      pageID: "roster",
      returnLabel: i18n.t("roster.returnToMyRoster", {
        ns: "ui",
        defaultValue: "Return to my roster",
      }),
      scrollY: getMobileScrollY(),
    });
  const injuryImpact = getRosterInjuryImpactStatus({
    injuredCount: injuries?.length ?? 0,
    longestAbsence: injuries?.reduce((max, injury) => Math.max(max, injury.games), 0) ?? 0,
  });

  return (
    <MobileSheet
      onClose={onClose}
      open={open}
      title={i18n.t("injuries.title", { ns: "ui", defaultValue: "Injuries" })}
    >
      {injuries === undefined ? (
        <div className="d-flex justify-content-center py-4" data-injuries-sheet-loading="">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">
              {i18n.t("common.loading", { ns: "ui", defaultValue: "Loading…" })}
            </span>
          </div>
        </div>
      ) : (
        <div
          data-injuries-sheet-content=""
          data-injuries-sheet-empty={injuries.length === 0 ? "" : undefined}
        >
          <div className={injuries.length > 0 ? "mb-3" : undefined}>
            <MobileStatusMetric
              detail={
                injuries.length === 0
                  ? i18n.t("injuries.allAvailable", {
                      ns: "ui",
                      team: teamName,
                      defaultValue: "{{team}} has no listed injuries for the next game.",
                    })
                  : i18n.t("injuries.nextGameImpact", {
                      ns: "ui",
                      count: injuries.length,
                      team: teamName,
                      defaultValue:
                        "{{count}} player on {{team}} may affect the next game rotation.",
                    })
              }
              label={i18n.t("injuries.yourTeamSummary", {
                ns: "ui",
                defaultValue: "Your team injuries",
              })}
              score={injuryImpact.score}
              value={getInjuryImpactText(injuryImpact.state)}
            />
          </div>
          {injuries.length > 0 ? (
            <div data-injuries-sheet-list="">
              {injuries.map((p) => (
                <MobileEntityRow
                  key={p.pid}
                  meta={
                    <>
                      <PositionText pos={p.pos} />
                      <span className="ms-2">{translateInjuryName(p.type)}</span>
                    </>
                  }
                  title={
                    <a href={playerHref(p.pid)}>{formatPlayerName(p.firstName, p.lastName)}</a>
                  }
                  trailing={
                    <span className="small text-nowrap text-body-secondary">
                      {i18n.t("roster.injuriesSheetGames", {
                        ns: "ui",
                        count: p.games,
                        defaultValue: "{{count}} game(s) out",
                      })}
                    </span>
                  }
                />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </MobileSheet>
  );
};
